import { FC, useEffect, useState } from "react";
import { Box, Container, Grid, IconButton, Link, Typography } from "@mui/material";
import logo from '../../assets/images/logo.png';
import googleplay from '../../assets/images/googleplay.png';
import iosbtn from '../../assets/images/iosbtn.png';
import peopleImg from '../../assets/images/people.png';
import { ArrowForward, Menu as MenuIcon, Close } from "@mui/icons-material";
import Features from "./features";
import Investor from "./investor";
import HouseSeeker from "./houseSeeker";
import Tenant from "./tenant";
import Landlord from "./landlord";
import Plans from "./plans";
import Referral from "./referral";
import Services from "./services";
import Developer from "./developers";
import Footer from "./footer";
import RotatingMessages from "./rotatingMessages";
import { Apis } from "../../helpers";
import { Connect, mapDispatchToProps, mapStateToProps } from "../../Redux";

const PRIMARY = '#8c7c0c';
const PRIMARY_DARK = '#6b5e09';

const NAV_LINKS = [
    { label: 'Features', url: '#features' },
    { label: 'Landlords', url: '#landlord' },
    { label: 'Tenants', url: '#tenant' },
    { label: 'Services', url: '#services' },
    { label: 'Pricing', url: '#plans' },
    { label: 'Referral', url: '#referral' },
    { label: 'Contact', url: '#contact' },
];

interface Props {
    setRedux?: (data: any) => void
    totalUsers?: number
}

const Landing: FC<Props> = ({ setRedux, totalUsers }) => {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        Apis({ module: 'Users', action: 'getNumberOfUsers' })
            .then((count: any) => { if (setRedux) setRedux({ totalUsers: count }) })
            .catch(() => { });

        const hero = document.getElementById('home')
        if (!hero) return
        const observer = new IntersectionObserver(
            ([entry]) => setScrolled(!entry.isIntersecting),
            { threshold: 0, rootMargin: '-64px 0px 0px 0px' }
        )
        observer.observe(hero)
        return () => observer.disconnect()
    }, []);


    return (
        <>
            {/* ─── Global animation CSS ─── */}
            <style>{`
                html, body { overflow-x: hidden; }
                .sg-reveal {
                    opacity: 0;
                    transform: translateY(28px);
                    transition: opacity .65s cubic-bezier(.16,1,.3,1), transform .65s cubic-bezier(.16,1,.3,1);
                }
                .sg-reveal.sg-visible { opacity: 1; transform: translateY(0); }
                .sg-reveal-left {
                    opacity: 0; transform: translateX(-32px);
                    transition: opacity .65s cubic-bezier(.16,1,.3,1), transform .65s cubic-bezier(.16,1,.3,1);
                }
                .sg-reveal-left.sg-visible { opacity: 1; transform: translateX(0); }
                .sg-reveal-right {
                    opacity: 0; transform: translateX(32px);
                    transition: opacity .65s cubic-bezier(.16,1,.3,1), transform .65s cubic-bezier(.16,1,.3,1);
                }
                .sg-reveal-right.sg-visible { opacity: 1; transform: translateX(0); }
                .sg-d1 { transition-delay: .1s; }
                .sg-d2 { transition-delay: .2s; }
                .sg-d3 { transition-delay: .3s; }
                .sg-d4 { transition-delay: .4s; }
                .sg-card-lift {
                    transition: transform .22s ease, box-shadow .22s ease;
                }
                .sg-card-lift:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 20px 40px rgba(0,0,0,.1) !important;
                }
                .sg-btn-primary {
                    display: inline-flex; align-items: center; gap: 6px;
                    background: ${PRIMARY}; color: #fff;
                    padding: 12px 28px; border-radius: 10px;
                    font-weight: 600; font-size: 16px;
                    text-decoration: none;
                    transition: background .2s ease, transform .15s ease;
                    border: none; cursor: pointer;
                }
                .sg-btn-primary:hover { background: ${PRIMARY_DARK}; transform: translateY(-1px); }
                .sg-btn-outline {
                    display: inline-flex; align-items: center; gap: 6px;
                    background: transparent; color: ${PRIMARY};
                    padding: 12px 28px; border-radius: 10px;
                    font-weight: 600; font-size: 16px;
                    text-decoration: none;
                    border: 1.5px solid ${PRIMARY};
                    transition: background .2s ease, color .2s ease;
                    cursor: pointer;
                }
                .sg-btn-outline:hover { background: ${PRIMARY}; color: #fff; }
                .sg-nav-link {
                    color: #374151; font-size: 15px; font-weight: 500;
                    text-decoration: none;
                    transition: color .15s ease;
                }
                .sg-nav-link:hover { color: ${PRIMARY}; }
                @keyframes float {
                    0%, 100% { transform: translateY(0); }
                    50%       { transform: translateY(-12px); }
                }
                .sg-float { animation: float 5s ease-in-out infinite; }
                @keyframes sg-counter {
                    from { opacity: 0; transform: translateY(12px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .sg-stat { animation: sg-counter .6s cubic-bezier(.16,1,.3,1) both; }
                .sg-stat:nth-child(1) { animation-delay: .05s; }
                .sg-stat:nth-child(2) { animation-delay: .15s; }
                .sg-stat:nth-child(3) { animation-delay: .25s; }
                .sg-stat:nth-child(4) { animation-delay: .35s; }
                @media (prefers-reduced-motion: reduce) {
                    .sg-reveal, .sg-reveal-left, .sg-reveal-right {
                        opacity: 1; transform: none; transition: none;
                    }
                    .sg-float { animation: none; }
                }
            `}</style>

            {/* ─── Sticky Nav ─── */}
            <Box
                component="nav"
                sx={{
                    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1100,
                    backgroundColor: scrolled || menuOpen ? '#fff' : 'rgba(255,255,255,0)',
                    backdropFilter: scrolled || menuOpen ? 'blur(12px)' : 'none',
                    boxShadow: scrolled ? '0 2px 12px rgba(0,0,0,.08)' : 'none',
                    transition: 'background-color .25s ease, box-shadow .25s ease',
                }}
            >
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
                        {/* Logo */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <img src={logo} alt="Sheltergram" style={{ height: 40, width: 40 }} />
                            <Typography sx={{ fontWeight: 700, fontSize: 20, color: PRIMARY }}>Sheltergram</Typography>
                        </Box>

                        {/* Desktop nav links */}
                        <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 3.5 }}>
                            {NAV_LINKS.map(({ label, url }) => (
                                <a key={label} href={url} className="sg-nav-link">{label}</a>
                            ))}
                        </Box>

                        {/* CTA + hamburger */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Link href="/auth" sx={{ display: { xs: 'none', sm: 'block' }, color: '#374151', fontWeight: 600, fontSize: 15, textDecoration: 'none', '&:hover': { color: PRIMARY } }}>
                                Login
                            </Link>
                            <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
                                <a href="/auth?signup=true" className="sg-btn-primary" style={{ padding: '9px 20px', fontSize: 14, display: 'flex' }}>
                                    Get Started <ArrowForward style={{ fontSize: 16 }} />
                                </a>
                            </Box>
                            <IconButton
                                sx={{ display: { xs: 'flex', lg: 'none' }, ml: 0.5, zIndex: 1200 }}
                                onClick={() => setMenuOpen(p => !p)}
                            >
                                {menuOpen ? <Close sx={{ color: PRIMARY }} /> : <MenuIcon sx={{ color: PRIMARY }} />}
                            </IconButton>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* ── Mobile menu — rendered outside the nav so nothing clips it ── */}
            <Box
                sx={{
                    display: { xs: 'block', lg: 'none' },
                    position: 'fixed',
                    top: 64,
                    right: 8,
                    width: 252,
                    zIndex: 1150,
                    bgcolor: '#fff',
                    borderRadius: '0 0 0 16px',
                    boxShadow: '0 12px 40px rgba(0,0,0,.18)',
                    overflow: 'hidden',
                    // slide in/out from right
                    transform: menuOpen ? 'translateX(0)' : 'translateX(100%)',
                    transition: 'transform .25s cubic-bezier(.4,0,.2,1)',
                    pointerEvents: menuOpen ? 'auto' : 'none',
                }}
            >
                <Box sx={{ p: 1.5 }}>
                    {[...NAV_LINKS, { label: 'Login', url: '/auth' }].map(({ label, url }) => (
                        <a
                            key={label}
                            href={url}
                            onClick={() => setMenuOpen(false)}
                            style={{
                                display: 'block',
                                padding: '10px 12px',
                                borderRadius: 8,
                                color: '#374151',
                                fontWeight: 500,
                                textDecoration: 'none',
                                fontSize: 15,
                                marginBottom: 2,
                            }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#f9f8f4')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        >
                            {label}
                        </a>
                    ))}
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #f0ece0' }}>
                        <a
                            href="/auth?signup=true"
                            onClick={() => setMenuOpen(false)}
                            className="sg-btn-primary"
                            style={{ display: 'block', textAlign: 'center', borderRadius: 10 }}
                        >
                            Get Started Free
                        </a>
                    </Box>
                </Box>
            </Box>

            {/* Backdrop */}
            {menuOpen && (
                <Box
                    onClick={() => setMenuOpen(false)}
                    sx={{
                        display: { xs: 'block', lg: 'none' },
                        position: 'fixed', inset: 0, zIndex: 1149,
                        bgcolor: 'rgba(0,0,0,.3)',
                    }}
                />
            )}

            {/* ─── Hero ─── */}
            <Box
                component="section"
                id="home"
                sx={{
                    minHeight: '100vh',
                    background: `linear-gradient(135deg, #faf8ee 0%, #f0eccc 40%, #e8e4b8 100%)`,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    pt: { xs: 12, md: 14 },
                    pb: { xs: 6, md: 8 },
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Background blobs */}
                <Box sx={{ position: 'absolute', top: -80, right: -80, width: 500, height: 500, borderRadius: '50%', background: `radial-gradient(circle, ${PRIMARY}18 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -60, left: -60, width: 400, height: 400, borderRadius: '50%', background: `radial-gradient(circle, ${PRIMARY}10 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        {/* Left: copy */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                display: 'inline-block', bgcolor: `${PRIMARY}15`, color: PRIMARY,
                                px: 2, py: 0.5, borderRadius: 20, fontSize: 13, fontWeight: 600, mb: 3
                            }}>
                                Kenya's #1 Property Management Platform
                            </Box>

                            <RotatingMessages />

                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 5 }}>
                                <a href="/auth?signup=true" className="sg-btn-primary">
                                    Get Started Free <ArrowForward style={{ fontSize: 18 }} />
                                </a>
                                <a href="#features" className="sg-btn-outline">
                                    Explore Features
                                </a>
                            </Box>

                            {/* App store badges */}
                            <Box>
                                <Typography sx={{ fontSize: 13, color: '#6b7280', fontWeight: 500, mb: 1.5 }}>Download the app</Typography>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <a href="https://apps.apple.com/us/app/sheltergram/id6698868923">
                                        <img src={iosbtn} alt="Download on App Store" loading="lazy" style={{ height: 44, width: 'auto', borderRadius: 8 }} />
                                    </a>
                                    <a href="https://play.google.com/store/apps/details?id=com.sheltergram.co">
                                        <img src={googleplay} alt="Get it on Google Play" loading="lazy" style={{ height: 44, width: 'auto', borderRadius: 8 }} />
                                    </a>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Right: hero image */}
                        <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
                            <img
                                src={peopleImg}
                                alt="Sheltergram app preview"
                                className="sg-float"
                                style={{ width: '100%', maxWidth: 500, objectFit: 'contain', filter: 'drop-shadow(0 20px 40px rgba(140,124,12,.15))' }}
                            />
                        </Grid>
                    </Grid>

                    {/* Stats strip */}
                    <Box sx={{
                        mt: { xs: 6, md: 8 },
                        pt: { xs: 4, md: 5 },
                        borderTop: '1px solid rgba(140,124,12,.15)',
                        display: 'grid',
                        gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)' },
                        gap: 3,
                    }}>
                        {[
                            { value: '500+', label: 'Active Properties' },
                            { value: totalUsers ? `${totalUsers.toLocaleString()}+` : '...', label: 'Registered Users' },
                            { value: '200+', label: 'Service Providers' },
                            { value: 'Ksh 50M+', label: 'Rent Processed' },
                        ].map(({ value, label }) => (
                            <Box key={label} className="sg-stat" sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                                <Typography sx={{ fontSize: { xs: 28, md: 34 }, fontWeight: 800, color: PRIMARY, lineHeight: 1 }}>
                                    {value}
                                </Typography>
                                <Typography sx={{ fontSize: 14, color: '#6b7280', mt: 0.5 }}>{label}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Container>
            </Box>

            {/* ─── Sections ─── */}
            <Features />
            <Investor />
            <Developer />
            <HouseSeeker />
            <Tenant />
            <Landlord />
            <Plans />
            <Box id="referral"><Referral /></Box>
            <Services />
            <Footer />
        </>
    );
};

export default Connect(mapStateToProps, mapDispatchToProps)(Landing);
